import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { products } from '@/lib/data'
import { useCart } from '@/contexts/CartContext'
import { gtmEvents } from '@/lib/gtm'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Minus, Plus, ShoppingCart, MessageCircle } from 'lucide-react'

const ProductDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const product = products.find((p) => p.id === id)
  const { addToCart } = useCart()

  const [quantity, setQuantity] = useState(1)
  const [size, setSize] = useState<string>('M')
  const [color, setColor] = useState<string>('Preto')

  useEffect(() => {
    if (product) {
      gtmEvents.viewItem({
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        item_category: product.category,
      })
    }
  }, [product])

  if (!product) {
    return (
      <div className="container py-20 text-center">Produto não encontrado.</div>
    )
  }

  const isApparel =
    product.category === 'Camisetas' || product.category === 'Moletons'

  const handleAddToCart = () => {
    addToCart(
      product,
      quantity,
      isApparel ? size : undefined,
      isApparel ? color : undefined,
    )
  }

  const handleBuyNow = () => {
    handleAddToCart()
    navigate('/checkout')
  }

  const handleWhatsApp = () => {
    const text = `Olá, quero saber mais sobre o item ${product.name}`
    window.open(
      `https://wa.me/5511999999999?text=${encodeURIComponent(text)}`,
      '_blank',
    )
    gtmEvents.custom('click_whatsapp', {
      location: 'pdp',
      product: product.name,
    })
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumbs */}
      <nav className="text-sm text-muted-foreground mb-8">
        Home / Shop / {product.category} /{' '}
        <span className="text-foreground font-medium">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Image */}
        <div className="aspect-square bg-white rounded-xl border p-4 flex items-center justify-center overflow-hidden relative group">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
          />
        </div>

        {/* Info */}
        <div className="space-y-6">
          <div>
            <div className="flex gap-2 mb-2">
              <Badge variant="secondary">{product.category}</Badge>
              {product.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              {product.name}
            </h1>
            <p className="text-2xl font-bold text-primary">
              R$ {product.price.toFixed(2).replace('.', ',')}
            </p>
          </div>

          <p className="text-muted-foreground text-lg leading-relaxed">
            {product.description}
          </p>

          <div className="space-y-4 pt-4 border-t">
            {isApparel && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tamanho</label>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P">P</SelectItem>
                      <SelectItem value="M">M</SelectItem>
                      <SelectItem value="G">G</SelectItem>
                      <SelectItem value="GG">GG</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cor</label>
                  <Select value={color} onValueChange={setColor}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Preto">Preto</SelectItem>
                      <SelectItem value="Branco">Branco</SelectItem>
                      <SelectItem value="Cinza">Cinza</SelectItem>
                      <SelectItem value="Azul">Azul</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Quantidade</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded-md">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="h-10 w-10"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center font-medium">
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                    className="h-10 w-10"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <div className="flex gap-3">
              <Button size="lg" className="flex-1" onClick={handleAddToCart}>
                <ShoppingCart className="mr-2 h-5 w-5" /> Adicionar
              </Button>
              <Button
                size="lg"
                variant="secondary"
                className="flex-1 border-primary border"
                onClick={handleBuyNow}
              >
                Comprar Agora
              </Button>
            </div>
            <Button
              variant="outline"
              size="lg"
              className="w-full text-green-600 border-green-200 hover:bg-green-50"
              onClick={handleWhatsApp}
            >
              <MessageCircle className="mr-2 h-5 w-5" /> Comprar pelo WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
